import React, { useState, useEffect, useMemo, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBell,
  faSignOutAlt,
  faUser,
  faDownload,
  faVolumeUp
} from "@fortawesome/free-solid-svg-icons";
import {
  Nav,
  Image,
  Navbar,
  Dropdown,
  Container,
  ListGroup,
  Button,
  Badge,
  Form,
} from "@themesberg/react-bootstrap";

import Profile3 from "../assets/img/default_avatar.png";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Routes } from "../routes";
import { logout } from "../redux/actions/auth";
import './navbar.css'
import toast from "react-hot-toast";
import WebSocketService from "./WebSocketService";
import NotificationToast from "./NotificationToast";
import NotificationItem from "./NotificationItem";
import { 
  groupNotifications, 
  sortNotificationsByPriority, 
  filterNotifications 
} from "../utils/notificationUtils";
import notificationSoundService from "../services/notificationSoundService";
import { NOTIFICATION_CATEGORIES } from "../constants/notificationTypes";
import MobileNavbar from "./mobile/MobileNavbar";
import { useResponsive } from "../hooks/useResponsive";

// Notifications arriving within this window are bundled into a single popup
// that updates live (e.g. "Pending Releases [5]") instead of stacking toasts
// the user has to close one by one.
const NOTIFICATION_AGGREGATION_WINDOW_MS = 2500;
const AGGREGATE_NOTIFICATION_TOAST_ID = "notification-aggregate";

const NOTIFICATION_PRIORITY_ORDER = ["URGENT", "IMPORTANT", "SUCCESS", "INFO"];

const getPriorityDuration = (priority) =>
  priority === "URGENT" ? Infinity : // stays until dismissed
  priority === "IMPORTANT" ? 15000 :
  priority === "SUCCESS" ? 5000 : 8000;


export default () => {
  const history = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const { isMobile } = useResponsive();
  const [notifications, setNotifications] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [showDropdown, setShowDropdown] = useState(false);

  // Buffer for notifications arriving in the current aggregation window.
  const notificationBufferRef = useRef([]);
  const finalizeTimerRef = useRef(null);
  const lastNotificationBatchRef = useRef([]);
  
  const userId = user?.username;
  const mda_name = user?.mda_name;

  // Process notifications with grouping, sorting, and filtering
  const processedNotifications = useMemo(() => {
    // Ensure notifications is an array and filter out any invalid entries
    const validNotifications = (notifications || []).filter(notification => 
      notification && typeof notification === 'object' && notification.id
    );
    
    let filtered = validNotifications;
    
    // Apply search filter
    if (searchTerm.trim()) {
      filtered = filtered.filter(notification => 
        (notification.message || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (notification.notification_type || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply category filter
    if (selectedCategory !== "all") {
      filtered = filtered.filter(notification => 
        notification.category === selectedCategory
      );
    }
    
    const grouped = groupNotifications(filtered);
    return sortNotificationsByPriority(grouped);
  }, [notifications, searchTerm, selectedCategory]);

  // Get unread count
  const unreadCount = useMemo(() => {
    return filterNotifications.unreadOnly(notifications).length;
  }, [notifications]);

  // Check if all notifications are read
  const areNotificationsRead = unreadCount === 0;

  useEffect(() => {
    let isMounted = true;
    if (userId && mda_name) {
      console.log("Connecting WebSocket for user:", userId, "MDA:", mda_name);
      WebSocketService.connect(userId, mda_name);
    }

    // Subscribe to new notifications
    WebSocketService.subscribeToNotifications((notification) => {
      if (!isMounted) return;
      console.log("Received notification:", notification);
      showNotificationToast(notification);
      setNotifications(prev => [notification, ...prev]);
    });

    // Subscribe to all notifications
    WebSocketService.subscribeToAllNotifications((allNotifications) => {
      if (!isMounted) return;
      console.log("Received all notifications:", allNotifications);
      const validNotifications = Array.isArray(allNotifications) ? allNotifications : [];
      setNotifications(validNotifications);
    });

    return () => {
      isMounted = false;
      clearTimeout(finalizeTimerRef.current);
      WebSocketService.subscribeToNotifications(() => {});
      WebSocketService.subscribeToAllNotifications(() => {});
      WebSocketService.disconnect();
    };
  }, [userId, mda_name]);

  const showNotificationToast = (notification) => {
    // Bundle notifications that arrive close together into ONE live popup.
    // Older notifications always remain available in the bell dropdown.

    // Ignore duplicates of a notification already in the current burst.
    if (notificationBufferRef.current.some((n) => n.id === notification.id)) return;

    notificationBufferRef.current.push(notification);

    // Show (or refresh) the single popup immediately, held open while the
    // burst window is active...
    renderNotificationToast(notificationBufferRef.current, Infinity);

    // ...then (re)start the window: once notifications quiet down, finalize.
    clearTimeout(finalizeTimerRef.current);
    finalizeTimerRef.current = setTimeout(
      finalizeNotificationBatch,
      NOTIFICATION_AGGREGATION_WINDOW_MS
    );
  };

  const renderNotificationToast = (batch, duration) => {
    const latest = batch[batch.length - 1];
    const isAggregate = batch.length > 1;
    // Offer a one-click "Approve All" only when the whole burst is unread
    // approval-type notifications.
    const isApproveAll =
      isAggregate &&
      batch.every((n) => n.category === "approval") &&
      filterNotifications.unreadOnly(batch).length === batch.length;
    const virtualNotification = isAggregate
      ? {
          ...latest,
          isGroup: true,
          count: batch.length,
          message: latest.message
            ? `${latest.message} (+${batch.length - 1} more)`
            : `${batch.length} new notifications`,
        }
      : latest;

    toast.custom(
      (t) => (
        <NotificationToast
          notification={virtualNotification}
          isVisible={t.visible}
          isAggregate={isAggregate}
          isApproveAll={isApproveAll}
          onAction={isAggregate ? handleAggregateAction : handleNotificationAction}
          onDismiss={dismissAggregateToast}
        />
      ),
      {
        duration, // held open during the burst; priority-based once finalized
        position: 'top-right',
        id: AGGREGATE_NOTIFICATION_TOAST_ID, // one live popup, updated in place
      }
    );
  };

  const finalizeNotificationBatch = () => {
    finalizeTimerRef.current = null;
    const batch = notificationBufferRef.current;
    notificationBufferRef.current = [];
    if (batch.length === 0) return;

    lastNotificationBatchRef.current = batch;

    // The highest-priority notification in the burst decides how long the
    // popup lingers (URGENT stays until dismissed).
    const batchPriority = [...new Set(batch.map((n) => n.priority || "INFO"))]
      .sort(
        (a, b) =>
          NOTIFICATION_PRIORITY_ORDER.indexOf(a) -
          NOTIFICATION_PRIORITY_ORDER.indexOf(b)
      )[0];

    renderNotificationToast(batch, getPriorityDuration(batchPriority));
  };

  const clearNotificationBuffer = () => {
    clearTimeout(finalizeTimerRef.current);
    finalizeTimerRef.current = null;
    notificationBufferRef.current = [];
  };

  const dismissAggregateToast = () => {
    toast.dismiss(AGGREGATE_NOTIFICATION_TOAST_ID);
    clearNotificationBuffer();
  };

  const markBatchAsRead = () => {
    const batch = lastNotificationBatchRef.current.length
      ? lastNotificationBatchRef.current
      : notificationBufferRef.current;
    batch.forEach((n) => n.id && markAsRead(n.id));
    lastNotificationBatchRef.current = [];
  };

  const handleAggregateAction = (actionType) => {
    switch (actionType) {
      case 'VIEW':
        // Navigate to the notification center to see every item in the burst
        history(Routes.Notifications.path);
        break;
      case 'APPROVE_ALL':
        // Quick-approve every notification in the burst, then close the popup
        // with a single confirmation (not one toast per item).
        {
          const batch = lastNotificationBatchRef.current.length
            ? lastNotificationBatchRef.current
            : notificationBufferRef.current;
          batch.forEach((n) => handleQuickApprove(n, { silent: true }));
          toast.success(
            `Approved ${batch.length} notification${batch.length === 1 ? '' : 's'}`
          );
          dismissAggregateToast();
        }
        break;
      case 'DISMISS':
        // Dismissing the bundled popup marks the whole burst as read
        markBatchAsRead();
        break;
      default:
        console.log('Unknown aggregate action:', actionType);
    }
  };

  const handleNotificationAction = (actionType, notification) => {
    console.log(`Action ${actionType} on notification:`, notification);
    
    switch (actionType) {
      case 'VIEW':
        // Navigate to notification details or relevant page
        history(Routes.Notifications.path);
        break;
        
      case 'APPROVE':
        // Handle quick approval
        handleQuickApprove(notification);
        break;
        
      case 'DEFER':
        // Handle defer (could show a time picker)
        handleDefer(notification);
        break;
        
      case 'SNOOZE':
        // Handle snooze functionality
        handleSnooze(notification);
        break;
        
      case 'DISMISS':
        // Mark as read and remove
        markAsRead(notification.id);
        break;
        
      default:
        console.log('Unknown action:', actionType);
    }
  };

  const handleQuickApprove = (notification, options = {}) => {
    // Implement quick approval logic
    console.log('Quick approve:', notification);
    if (!options.silent) {
      toast.success('Quick approval initiated');
    }
    markAsRead(notification.id);
  };

  const handleDefer = (notification) => {
    // Implement defer logic (could set a reminder)
    console.log('Defer notification:', notification);
    toast('Notification deferred for 1 hour');
    markAsRead(notification.id);
  };

  const handleSnooze = (notification, duration = 3600000) => { // Default 1 hour
    // Mark as read and set a timeout to show again
    markAsRead(notification.id);
    
    setTimeout(() => {
      // Re-add notification after snooze duration
      setNotifications(prev => [
        { ...notification, id: Date.now(), status: 'unread', snoozed: true },
        ...prev
      ]);
      toast(`Snoozed notification: ${notification.notification_type}`);
    }, duration);
    
    toast.success(`Notification snoozed for ${duration / 60000} minutes`);
  };

  const exportNotifications = () => {
    try {
      const exportData = notifications.map(notification => ({
        type: notification.notification_type,
        message: notification.message,
        category: notification.category,
        priority: notification.priority,
        status: notification.status,
        created_at: notification.created_at,
        mda_name: notification.mda_name
      }));
      
      const dataStr = JSON.stringify(exportData, null, 2);
      const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
      
      const exportFileDefaultName = `notifications_${new Date().toISOString().split('T')[0]}.json`;
      
      const linkElement = document.createElement('a');
      linkElement.setAttribute('href', dataUri);
      linkElement.setAttribute('download', exportFileDefaultName);
      linkElement.click();
      
      toast.success('Notifications exported successfully!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export notifications');
    }
  };

  const handleToggle = (isOpen) => {
    setShowDropdown(isOpen);
  };

  const handleViewAllClick = () => {
    history(Routes.Notifications.path);
    setShowDropdown(false);
  };

  const handleLogout = () =>
    dispatch(logout(() => history(Routes.Signin.path)));

  const markAsRead = (id) => {
    WebSocketService.markAsRead(id);
    setNotifications((prevNotifications) =>
      prevNotifications.map((notification) =>
        notification.id === id
          ? { ...notification, status: 1 }
          : notification
      )
    );
  };

  const markAllAsRead = () => {
    WebSocketService.markAllAsRead(userId);
    setNotifications(prev =>
      prev.map(notification => ({ ...notification, status: 1 }))
    );
    toast.success('All notifications marked as read');
  };

  const testNotificationSound = () => {
    notificationSoundService.playSound('info');
    toast.success('Test sound played!');
  };

  // Show mobile navbar on mobile devices
  if (isMobile) {
    return (
      <MobileNavbar 
        notifications={notifications}
        unreadCount={unreadCount}
      />
    );
  }

  return (
    <>
      <Navbar
        expanded
        className="ps-0 pe-2 pb-0 bg-primary d-none d-md-block"
        style={{
          borderRadius: "15px",
          width: "99%",
          margin: "5px",
          marginInline: "auto",
          padding: "10px",
        }}
      >
        <Container fluid className="px-0">
          <div className="d-flex justify-content-between align-items-center w-100">
            <center>
              <h4
                style={{ color: "white", marginLeft: 5, fontWeight: "bold" }}
                className="display-sm-5"
              >
                KATSINA STATE INTEGRATED FINANCIAL MANAGEMENT INFORMATION SYSTEM
                <br />
                <span style={{ marginLeft: "18px", fontSize: "xs" }}>
                  (KIFMIS)
                </span>
              </h4>
              {user && user.mda_name && user.mda_name !== "" ? (
                <h4
                  style={{ color: "white", marginLeft: 5, fontWeight: "bold" }}
                >
                  {user && user.mda_name}
                </h4>
              ) : null}
            </center>

            <Nav className="align-items-center">
              <Dropdown
                as={Nav.Item}
                show={showDropdown}
                onToggle={handleToggle}
                autoClose="auto"
              >
                <Dropdown.Toggle
                  as={Nav.Link}
                  id="dropdown-autoclose-outside"
                  className="text-dark icon-notifications me-lg-3 position-relative"
                >
                  <span className="icon icon-sm">
                    <FontAwesomeIcon
                      icon={faBell}
                      className={`bell-shake text-white ${!areNotificationsRead ? 'notification-bell-active' : ''}`}
                    />
                    {!areNotificationsRead && (
                      <Badge 
                        bg="danger" 
                        className="position-absolute top-0 start-100 translate-middle rounded-pill"
                        style={{ fontSize: '0.7rem' }}
                      >
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </Badge>
                    )}
                  </span>
                </Dropdown.Toggle>
                
                <Dropdown.Menu className="dashboard-dropdown notifications-dropdown dropdown-menu-lg dropdown-menu-center mt-2 py-0">
                  <ListGroup className="list-group-flush">
                    {/* Header with Search and Filters */}
                    <div className="p-3 border-bottom">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="mb-0 fw-bold">Notifications</h6>
                        <div className="d-flex gap-2">
                          {!areNotificationsRead && (
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0 text-primary"
                              onClick={markAllAsRead}
                            >
                              Mark all read
                            </Button>
                          )}
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0 text-warning"
                            onClick={testNotificationSound}
                            title="Test notification sound"
                          >
                            <FontAwesomeIcon icon={faVolumeUp} />
                          </Button>
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0 text-success"
                            onClick={exportNotifications}
                            title="Export notifications"
                          >
                            <FontAwesomeIcon icon={faDownload} />
                          </Button>
                        </div>
                      </div>
                      
                      {/* Search Input */}
                      <div className="mb-2">
                        <Form.Control
                          type="text"
                          placeholder="Search notifications..."
                          size="sm"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="border-0 bg-light"
                        />
                      </div>
                      
                      {/* Category Filter */}
                      <div>
                        <Form.Select
                          size="sm"
                          value={selectedCategory}
                          onChange={(e) => setSelectedCategory(e.target.value)}
                          className="border-0 bg-light"
                        >
                          <option value="all">All Categories</option>
                          <option value={NOTIFICATION_CATEGORIES.APPROVAL}>Approvals</option>
                          <option value={NOTIFICATION_CATEGORIES.RELEASE}>Releases</option>
                          <option value={NOTIFICATION_CATEGORIES.PAYMENT}>Payments</option>
                          <option value={NOTIFICATION_CATEGORIES.SYSTEM}>System</option>
                          <option value={NOTIFICATION_CATEGORIES.REMINDER}>Reminders</option>
                        </Form.Select>
                      </div>
                      
                      {/* Filter Results Info */}
                      {(searchTerm || selectedCategory !== "all") && (
                        <div className="mt-2">
                          <small className="text-muted">
                            Showing {processedNotifications.length} of {notifications.length} notifications
                            {searchTerm && (
                              <Button
                                variant="link"
                                size="sm"
                                className="p-0 ms-2 text-danger"
                                onClick={() => setSearchTerm("")}
                              >
                                Clear search
                              </Button>
                            )}
                          </small>
                        </div>
                      )}
                    </div>

                    {/* Notifications List */}
                    {processedNotifications.length > 0 ? (
                      <>
                        {filterNotifications.recent(processedNotifications, 5).map((notification) => (
                          <NotificationItem
                            key={notification.id}
                            notification={notification}
                            onAction={handleNotificationAction}
                            onMarkAsRead={markAsRead}
                          />
                        ))}
                      </>
                    ) : (
                      <ListGroup.Item className="text-center py-4">
                        <div className="text-muted">
                          <FontAwesomeIcon icon={faBell} size="2x" className="mb-2" />
                          <p className="mb-0">No notifications</p>
                        </div>
                      </ListGroup.Item>
                    )}

                    {/* Footer */}
                    <Dropdown.Item
                      className="text-center text-primary fw-bold py-3 border-top"
                      onClick={handleViewAllClick}
                    >
                      View all notifications
                      {processedNotifications.length > 5 && (
                        <Badge bg="secondary" className="ms-2">
                          +{processedNotifications.length - 5}
                        </Badge>
                      )}
                    </Dropdown.Item>
                  </ListGroup>
                </Dropdown.Menu>
              </Dropdown>

              <Dropdown as={Nav.Item}>
                <Dropdown.Toggle as={Nav.Link} className="pt-1 px-0">
                  <div className="media d-flex align-items-center">
                    <Image
                      src={Profile3}
                      className="user-avatar md-avatar rounded-circle"
                    />
                    <div className="media-body ms-2 text-dark align-items-center d-none d-lg-block">
                      <span className="mb-0 font-small fw-bold text-white">
                        {/* {user.fullname} */}
                      </span>
                    </div>
                  </div>
                </Dropdown.Toggle>
                <Dropdown.Menu className="user-dropdown dropdown-menu-right mt-2">
                  <Dropdown.Item
                    className="fw-bold"
                    onClick={() => history(Routes.pv.UserProfile)}
                  >
                    <FontAwesomeIcon
                      icon={faUser}
                      className="text-danger me-2"
                    />{" "}
                    Profile
                  </Dropdown.Item>

                  <Dropdown.Item className="fw-bold" onClick={handleLogout}>
                    <FontAwesomeIcon
                      icon={faSignOutAlt}
                      className="text-danger me-2"
                    />{" "}
                    Logout
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </Nav>
          </div>
        </Container>
      </Navbar>
    </>
  );
};
